import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import { push } from '@lagunovsky/redux-react-router'
import { PATH_NAME } from 'app/constants'
import store from 'app/store'
import { startLoading, stopLoading } from 'components/Loader/LoaderPopup/reducer'
import { requireCinemaId } from 'components/Modal/CinemaSelectWindow/saga'
import { setErrorMessage, setSuccessMessage } from 'components/Windows/CentralMessagesList/reducer'
import { callApiAsync } from 'services/api/callAPIAsync'
import { API_LANGUAGE_MAPPING } from '../../../../global/i18n/constants'
import { localeSelector } from '../../../../global/i18n/selectors'
import { getCinemaSlug } from '../../../Hall/helpers'
import {
    cancelOrder,
    cancelReservation,
    getAccountOrders,
    payReservation,
    restoreToCard,
    setError,
    setLoading,
    setReservations,
    setTickets,
} from './reducer'

function* getDataGql(ticket) {
    try {
        const movieId = ticket.event.title_groupid
        const site = getCinemaSlug(ticket.event.cinemaid)
        const responseGql = yield call(callApiAsync, 'getGqlEventData', { movieId, site })
        const entry = responseGql?.data?.data?.entry

        const posterUrl = entry?.PictureUrl ?? entry?.movie[0]?.PictureUrl ?? entry?.movieGroup[0]?.PictureUrl

        const dataGql = {
            genres: entry?.movie[0]?.CSFD[0]?.Genres,
            imageUrl: entry?.movie[0]?.CSFD[0]?.Photos[0]?.Url,
            movieSubtitles: entry?.movie[0]?.MovieSubtitles,
            posterUrl: posterUrl,
        }

        const updatedTicket = {
            ...ticket,
            event: {
                ...ticket.event,
                ...dataGql,
            },
        }
        return updatedTicket
    } catch (e) {
        const dataGql = {
            genres: [],
            imageUrl: '',
            movieSubtitles: [],
            posterUrl: '',
        }
        console.error.e
        const updatedTicket = {
            ...ticket,
            event: {
                ...ticket.event,
                ...dataGql,
            },
        }
        return updatedTicket
    }
}

function* addAllGqlData(data) {
    const dataWithGql = yield all(
        data?.map(function* (item) {
            const updatedTickets = yield all(item.items?.items_tickets?.map((ticket) => getDataGql(ticket)))

            const updatedItem = {
                ...item,
                items: {
                    ...item.items,
                    items_tickets: updatedTickets,
                },
            }

            return updatedItem
        })
    )
    return dataWithGql
}

function* getOrdersData(payload) {
    const { data, error } = yield call(callApiAsync, 'ordersGet', { ...payload })

    if (error) {
        console.error(error)
        setErrorMessage(error)
        return { error: true }
    }

    const dataWithGql = yield call(addAllGqlData, data)

    return { data: dataWithGql }
}

function* getBookingsData(payload) {
    const { data, error } = yield call(callApiAsync, 'bookingsGet', { ...payload })

    if (error) {
        console.error(error)
        setErrorMessage(error)
        return { error: true }
    }

    const dataWithGql = yield call(addAllGqlData, data)

    return { data: dataWithGql }
}

export function* ordersCallSaga() {
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null

    yield all([put(setLoading(true)), put(setError(null))])

    const ordersData = yield call(getOrdersData, { language })
    const bookingsData = yield call(getBookingsData, { language })

    if (ordersData?.error || bookingsData?.error) {
        yield all([put(setError(ordersData?.error ?? bookingsData?.error)), put(setLoading(false))])
        return
    }

    yield all([put(setTickets(ordersData?.data)), put(setReservations(bookingsData?.data)), put(setLoading(false))])
}

//In progress
function* cancelReservationSaga({ payload }) {
    yield put(startLoading())
    const { error } = yield call(callApiAsync, 'bookingCancel', { ...payload })
    yield put(stopLoading())
    if (error) {
        console.error(error)
        yield put(setErrorMessage(error))
        return
    }

    yield put(setSuccessMessage('CANCEL_RESERVATION'))
    yield put(getAccountOrders())
}

//Hardcoded for GoPay
function* payReservationSaga({ payload }) {
    const bookingId = payload.toString()
    localStorage.setItem('activeBookingId', bookingId)
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null

    const url = window.location.origin
    const payment_method = 'Gopay'

    const payment_url_return = `${url}${PATH_NAME.VERIFY_PAYMENT}`
    const payment_failed_url_return = `${url}${PATH_NAME.PAYMENT_FAILED}`

    yield put(startLoading())

    const { data: paymentData, error: paymentError } = yield call(callApiAsync, 'paymentCreate', {
        bookingid: bookingId,
        payment_method,
        language,
        payment_url_return,
        payment_failed_url_return,
    })

    yield put(stopLoading())

    if (paymentError) {
        console.error(paymentError)
        yield put(setErrorMessage(paymentError))
        return
    }

    const paymentUrl = paymentData?.payment_params?.UrlPaymentGate

    try {
        // global method from form script
        yield _gopay.checkout({ gatewayUrl: paymentUrl, inline: true }, (goPayResult) =>
            store.dispatch(push(goPayResult?.url))
        )
    } catch (err) {
        console.error('paymentStartCallSaga', err)
        yield put(setError({ internal: true, message: err }))
    }
}

function* restoreToCardSaga({ payload }) {
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null
    const cinemaid = yield call(requireCinemaId)
    if (!cinemaid) return
    const { data, error } = yield call(callApiAsync, 'bookingRestoreToCard', { ...payload, cinemaid, language })
    if (error) {
        console.error(error)
        yield put(setErrorMessage(error))
        return
    }
    yield put(push(PATH_NAME.YOUR_ORDER))
}

function* cancelOrderSaga({ payload }) {
    yield put(startLoading())
    const { data, error } = yield call(callApiAsync, 'paymentCancel', { ...payload })
    yield put(stopLoading())
    if (error) {
        console.error(error)
        yield put(setErrorMessage(error))
        return
    }
    yield put(setSuccessMessage('CANCEL_ORDER'))
    yield put(getAccountOrders())
}

export default function* accountOrdersSaga() {
    yield all([
        takeLatest(getAccountOrders.type, ordersCallSaga),
        takeLatest(cancelReservation.type, cancelReservationSaga),
        takeLatest(payReservation.type, payReservationSaga),
        takeLatest(restoreToCard.type, restoreToCardSaga),
        takeLatest(cancelOrder.type, cancelOrderSaga),
    ])
}
